import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Sparkles, Brain, Code, Cloud, Award } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SkillsPage = () => {
  const [skills, setSkills] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [skillsRes, matrixRes] = await Promise.all([
        axios.get(`${API_URL}/api/skills`, { withCredentials: true }),
        axios.get(`${API_URL}/api/skills-matrix`, { withCredentials: true })
      ]);
      setSkills(skillsRes.data);
      setMatrix(matrixRes.data);
    } catch (error) {
      console.error('Fetch skills error:', error);
      toast.error('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const maxTechCount = Math.max(...(matrix?.tech_stack_distribution?.map(t => t.count) || [1]));
  const maxAICount = Math.max(...(matrix?.ai_models_distribution?.map(t => t.count) || [1]));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="skills-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-primary" />
          Skills & Capability Matrix
        </h1>
        <p className="text-muted-foreground">Track and visualize team skills and expertise</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tech Stack Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-primary" />
              Tech Stack Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {matrix?.tech_stack_distribution?.length > 0 ? (
              <div className="space-y-4">
                {matrix.tech_stack_distribution.map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-mono text-xs skill-badge bg-transparent border-0 p-0">{item.skill}</span>
                      <span className="text-muted-foreground">{item.count} engineers</span>
                    </div>
                    <Progress value={(item.count / maxTechCount) * 100} className="h-2" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No tech stack data available</p>
            )}
          </CardContent>
        </Card>

        {/* AI Models Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[hsl(var(--ai-accent))]" />
              AI/ML Models Expertise
            </CardTitle>
          </CardHeader>
          <CardContent>
            {matrix?.ai_models_distribution?.length > 0 ? (
              <div className="space-y-4">
                {matrix.ai_models_distribution.map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <Badge className="badge-ai font-mono text-xs">{item.model}</Badge>
                      <span className="text-muted-foreground">{item.count} engineers</span>
                    </div>
                    <Progress value={(item.count / maxAICount) * 100} className="h-2 [&>div]:bg-[hsl(var(--ai-accent))]" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No AI models data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Individual Skills */}
      <Card>
        <CardHeader>
          <CardTitle>Team Skills Overview</CardTitle>
        </CardHeader>
        <CardContent>
          {skills.length > 0 ? (
            <div className="space-y-6">
              {skills.map((skill, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Code className="w-3 h-3" /> Tech Stack
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {skill.tech_stack?.map((tech, i) => (
                          <Badge key={i} className="skill-badge text-xs">{tech}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Brain className="w-3 h-3" /> AI Models
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {skill.ai_models?.map((model, i) => (
                          <Badge key={i} className="badge-ai font-mono text-xs">{model}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Cloud className="w-3 h-3" /> Deployment
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {skill.deployment_experience?.map((exp, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{exp}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Award className="w-3 h-3" /> Certifications
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {skill.certifications?.slice(0, 2).map((cert, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                        ))}
                        {skill.certifications?.length > 2 && (
                          <Badge variant="outline" className="text-xs">+{skill.certifications.length - 2}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No skills data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SkillsPage;
